import React, {useEffect, useMemo, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {useLocation, useRouteMatch} from 'react-router-dom'
import {
  Button,
  makeStyles,
  MenuItem,
  OutlinedInput,
  Select,
} from '@material-ui/core'
import IconDownload from 'pages/HealthCenter/Report/Elements/IconDownload.svg'
import Logo from 'pages/HealthCenter/Report/Elements/logo.svg'
import {Locale} from 'components/atoms/TranslationSelect'
import useLanguageReport from 'hooks/useLanguageReport'
import {getAccessToken} from 'api/storageApi'
import {getQueryFromUrl} from 'helpers/commonHelper'
import {TYPE_REPORT} from 'pages/Analysis/EEGSummary/constants'
import EEGSummaryReportStyle from 'pages/Analysis/EEGSummary/style'
import FiveIndicatorsReport from 'pages/Analysis/EEGSummary/Report/FiveIndicators'
import BrainFunctionConnectivity from 'pages/Analysis/EEGSummary/Report/BrainFunctionConnectivity'
import BrainFunctionMapping from 'pages/Analysis/EEGSummary/Report/BrainFunctionMapping'
import {
  createEEGSummaryPDFReport,
  createFollowupPDFReport,
} from 'api/analysisApi'
import {downloadFileFromWsUrl} from 'helpers/fileHelper'
import useLanguage from 'features/languageReport/useLanguage'

const useStyles = makeStyles(EEGSummaryReportStyle)

function Report() {
  const classes = useStyles()
  const {t, i18n} = useTranslation()
  const BASE_LANG = [
    {
      label: t('IKoranLang'),
      value: 'ko',
      disabled: false,
    },
    {
      label: t('IEnglishLang'),
      value: 'en',
      disabled: false,
    },
    {
      label: t('IJapanesesLang'),
      value: 'ja',
      disabled: false,
    },
    {
      label: t('ISpanishLang'),
      value: 'es',
      disabled: false,
    },
    {
      label: t('IRomanianLang'),
      value: 'ro',
      disabled: false,
    },
    {
      label: t('IThaiLang'),
      value: 'th',
      disabled: false,
    },
    {
      label: t('IChineseLang'),
      value: 'zh',
      disabled: false,
    },
  ]
  const location = useLocation()
  const {
    params: {id},
  } = useRouteMatch<{id: string}>()

  const [isChild, setIsChild] = useState<boolean>(false)
  const [tabActive, setTabActive] = useState(0)
  const [countChangeLang, setCountChangeLang] = useState<number>(0)
  const [currentLocale, setCurrentLocale] = useState<string>(
    i18n.language.toString(),
  )
  const {textHealthAPI} = useLanguage('eeg')

  const handleChangeLang = async (value: Locale) => {
    const count = countChangeLang
    setCountChangeLang(count + 1)
    // setLanguage(value)
    await i18n.changeLanguage(value)
    setCurrentLocale(value)
  }
  const [loadingDownload, setLoadingDownload] = useState(false)
  const handleClickDownLoad = async () => {
    try {
      setLoadingDownload(true)
      let createResponse
      if (tabActive === TYPE_REPORT.BRAIN_FUNCTION_MAPPING) {
        createResponse = await createEEGSummaryPDFReport(
          [Number.parseInt(id, 10)],
          i18n.language,
        )
      } else {
        createResponse = await createFollowupPDFReport(
          [Number.parseInt(id, 10)],
          i18n.language,
        )
      }
      await downloadFileFromWsUrl(createResponse.ws_url, getAccessToken() ?? '')
    } catch (error) {
      console.error(error)
    } finally {
      setLoadingDownload(false)
    }
  }

  const getNameTab = useMemo(() => {
    if (tabActive === TYPE_REPORT.BRAIN_FUNCTION_MAPPING) {
      if (isChild) {
        return t('IEEGChildSummaryReportHeader')
      }
      return t('IEEGSummaryReportHeader')
    }
    if (tabActive === TYPE_REPORT.FIVE_INDICATORS) {
      return t('IFiveBrainFunctionIndicatorsReport')
    }
    if (tabActive === TYPE_REPORT.BRAIN_FUNCTION_CONNECTIVITY) {
      return t('IBrainConnectivityReport')
    }
    return ''
  }, [tabActive, i18n.language, isChild])

  useEffect(() => {
    const {type} = getQueryFromUrl(location.search)
    setTabActive(type ?? 1)

    window.scroll({
      top: 0,
      left: 0,
      behavior: 'smooth',
    })
  }, [])

  const {isLocalLanguage} = useLanguageReport('health')
  useLanguageReport('eeg')

  return (
    <div>
      <div className={classes.root}>
        <div className={classes.topRoot}>
          <div className={`${classes.topNav} no-print`}>
            <div className={classes.topNavLeft}>
              <div className={classes.logo}>
                <img src={Logo} alt='Logo' />
              </div>
              <div className={classes.tabs}>
                <div
                  className={`${classes.tab}`}
                  aria-hidden='true'
                  style={{
                    color: '#00A29C',
                    fontWeight: 'bold',
                  }}
                >
                  {getNameTab}
                </div>
              </div>
            </div>
            <div className={classes.topNavRight}>
              <div>
                <Select
                  value={currentLocale}
                  onChange={(e) => handleChangeLang(e.target.value as Locale)}
                  className={classes.searchSelect}
                  input={<OutlinedInput name='age' id='outlined-age-simple' />}
                >
                  {BASE_LANG.map((item) => (
                    <MenuItem
                      value={item.value}
                      key={item.value}
                      disabled={item.disabled ?? false}
                    >
                      {t(item.label)}
                    </MenuItem>
                  ))}
                </Select>
              </div>
              <Button disabled={loadingDownload} onClick={handleClickDownLoad}>
                <img src={IconDownload} alt='IconDownload' />
              </Button>
            </div>
          </div>
          <div id='health-center' className='no-print'>
            {tabActive === TYPE_REPORT.BRAIN_FUNCTION_MAPPING && (
              <BrainFunctionMapping
                setIsChild={(value: boolean) => {
                  setIsChild(value)
                }}
                textHealthAPI={textHealthAPI}
              />
            )}

            {tabActive === TYPE_REPORT.FIVE_INDICATORS && (
              <FiveIndicatorsReport
                isLocalLanguage={isLocalLanguage}
                textHealthAPI={textHealthAPI}
              />
            )}
            {tabActive === TYPE_REPORT.BRAIN_FUNCTION_CONNECTIVITY && (
              <BrainFunctionConnectivity textHealthAPI={textHealthAPI} />
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default Report
